import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import UserPickerList from './UserPickerList';
import { bulkUserAction } from './api';
import type { AdminUser, BulkRowResult, UsersMeta } from './types';
import { fieldClass, labelClass, primaryBtnClass } from '../styles';

interface BulkEditUserProps {
  meta: UsersMeta;
}

type BulkAction =
  | 'set_organization'
  | 'set_user_type'
  | 'set_catalyx_role'
  | 'set_client_role'
  | 'deactivate';

const BulkEditUser: React.FC<BulkEditUserProps> = ({ meta }) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<AdminUser[]>([]);
  const [listKey, setListKey] = useState(0);
  const [action, setAction] = useState<BulkAction>('set_organization');
  const [organizationId, setOrganizationId] = useState<number | ''>('');
  const [userTypeId, setUserTypeId] = useState<number | ''>('');
  const [catalyxRoleId, setCatalyxRoleId] = useState<number | ''>('');
  const [clientRoleId, setClientRoleId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [results, setResults] = useState<BulkRowResult[] | null>(null);

  const selectedTypeKey = meta.user_types.find((t) => t.id === userTypeId)?.key;
  const hasCatalyx = selectedUsers.some((u) => u.user_type_key === 'catalyx');
  const hasClient = selectedUsers.some((u) => u.user_type_key === 'client');
  const editTypes = meta.user_types.filter((t) =>
    t.key === 'catalyx' ? meta.permissions.edit_catalyx : meta.permissions.edit_client
  );

  const actionDisabled = useMemo(() => {
    if (action === 'set_catalyx_role' && !hasCatalyx) return true;
    if (action === 'set_client_role' && !hasClient) return true;
    return false;
  }, [action, hasCatalyx, hasClient]);

  const apply = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one user.');
      return;
    }
    if (action === 'deactivate') {
      if (!window.confirm(`Deactivate ${selectedIds.length} account(s)?`)) return;
    }
    const payload: Record<string, unknown> = {
      user_ids: selectedIds,
      action,
    };
    if (action === 'set_organization') {
      if (!organizationId) {
        setError('Choose an organization.');
        return;
      }
      payload.organization_id = organizationId;
    }
    if (action === 'set_user_type') {
      if (!userTypeId) {
        setError('Choose a user type.');
        return;
      }
      payload.user_type_id = userTypeId;
      if (selectedTypeKey === 'catalyx') {
        if (!catalyxRoleId) {
          setError('Choose a Catalyx role.');
          return;
        }
        payload.catalyx_role_id = catalyxRoleId;
      }
      if (selectedTypeKey === 'client') {
        if (!clientRoleId) {
          setError('Choose a Client role.');
          return;
        }
        payload.client_role_id = clientRoleId;
      }
    }
    if (action === 'set_catalyx_role') {
      if (!catalyxRoleId) {
        setError('Choose a Catalyx role.');
        return;
      }
      payload.catalyx_role_id = catalyxRoleId;
    }
    if (action === 'set_client_role') {
      if (!clientRoleId) {
        setError('Choose a Client role.');
        return;
      }
      payload.client_role_id = clientRoleId;
    }

    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const payloadRes = await bulkUserAction(payload);
      setResults(payloadRes.results);
      setSummary(
        `Updated ${payloadRes.updated}` +
          (payloadRes.errors ? `, ${payloadRes.errors} could not be updated.` : '.')
      );
      setListKey((n) => n + 1);
      setSelectedIds([]);
      setSelectedUsers([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <UserPickerList
        meta={meta}
        mode="multi"
        selectedIds={selectedIds}
        refreshToken={listKey}
        onChange={(ids, users) => {
          setSelectedIds(ids);
          setSelectedUsers((prev) => {
            const map = new Map(prev.map((u) => [u.id, u]));
            users.forEach((u) => map.set(u.id, u));
            return ids
              .map((id) => map.get(id))
              .filter((u): u is AdminUser => Boolean(u));
          });
        }}
      />

      <div className="max-w-3xl border-t border-[#E8E8E8] pt-5">
        <h3 className="text-base font-semibold text-[#0C0B0B] mb-3">Bulk action</h3>
        {error && (
          <div className="mb-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
        )}
        {summary && (
          <div className="mb-3 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
            {summary}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as BulkAction)}
              className={fieldClass}
            >
              <option value="set_organization">Change organization</option>
              <option value="set_user_type">Change user type</option>
              <option value="set_catalyx_role">Change Catalyx role</option>
              <option value="set_client_role">Change Client role</option>
              <option value="deactivate">Deactivate</option>
            </select>
          </div>
          {action === 'set_organization' && (
            <div>
              <label className={labelClass}>Organization</label>
              <select
                value={organizationId}
                onChange={(e) =>
                  setOrganizationId(e.target.value ? Number(e.target.value) : '')
                }
                className={fieldClass}
              >
                <option value="">Select an organization</option>
                {meta.organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {action === 'set_user_type' && (
            <>
              <div>
                <label className={labelClass}>User type</label>
                <select
                  value={userTypeId}
                  onChange={(e) => {
                    const next = e.target.value ? Number(e.target.value) : '';
                    setUserTypeId(next);
                    setCatalyxRoleId('');
                    setClientRoleId('');
                  }}
                  className={fieldClass}
                >
                  <option value="">Select a user type</option>
                  {editTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedTypeKey === 'catalyx' && (
                <div>
                  <label className={labelClass}>Catalyx role</label>
                  <select
                    value={catalyxRoleId}
                    onChange={(e) =>
                      setCatalyxRoleId(e.target.value ? Number(e.target.value) : '')
                    }
                    className={fieldClass}
                  >
                    <option value="">Select a Catalyx role</option>
                    {meta.catalyx_roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {selectedTypeKey === 'client' && (
                <div>
                  <label className={labelClass}>Client role</label>
                  <select
                    value={clientRoleId}
                    onChange={(e) =>
                      setClientRoleId(e.target.value ? Number(e.target.value) : '')
                    }
                    className={fieldClass}
                  >
                    <option value="">Select a Client role</option>
                    {meta.client_roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
          {action === 'set_catalyx_role' && (
            <div>
              <label className={labelClass}>Catalyx role</label>
              <select
                value={catalyxRoleId}
                onChange={(e) =>
                  setCatalyxRoleId(e.target.value ? Number(e.target.value) : '')
                }
                className={fieldClass}
              >
                <option value="">Select a Catalyx role</option>
                {meta.catalyx_roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {!hasCatalyx && selectedIds.length > 0 && (
                <p className="mt-1 text-[12px] text-[#8A8A8A]">
                  None of the selected accounts are Catalyx users.
                </p>
              )}
            </div>
          )}
          {action === 'set_client_role' && (
            <div>
              <label className={labelClass}>Client role</label>
              <select
                value={clientRoleId}
                onChange={(e) =>
                  setClientRoleId(e.target.value ? Number(e.target.value) : '')
                }
                className={fieldClass}
              >
                <option value="">Select a Client role</option>
                {meta.client_roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {!hasClient && selectedIds.length > 0 && (
                <p className="mt-1 text-[12px] text-[#8A8A8A]">
                  None of the selected accounts are Client users.
                </p>
              )}
            </div>
          )}
        </div>
        <div className="mt-4">
          <button
            type="button"
            disabled={busy || selectedIds.length === 0 || actionDisabled}
            onClick={() => void apply()}
            className={primaryBtnClass}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
            Apply to {selectedIds.length || 0} user{selectedIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>

      {results && (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
              <tr>
                <th className="px-3 py-2.5 font-semibold">Email</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr key={`${row.id}-${row.email}`} className="border-t border-[#E8E8E8]">
                  <td className="px-3 py-2">{row.email || '—'}</td>
                  <td className="px-3 py-2 capitalize">{row.status}</td>
                  <td
                    className={`px-3 py-2 ${
                      row.status === 'error' ? 'text-red-700' : 'text-[#4A4A4A]'
                    }`}
                  >
                    {row.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BulkEditUser;
